/**
 * Reframe an icon mark's viewBox to its own INK, so one host box lands the same amount of drawn
 * mark — and the same ground line — for every icon family.
 *
 * Every family's viewBox bakes in a different amount of whitespace, so sizing the BOX sizes the
 * families differently and drops each at its own height over the line. That is what the per-family
 * margin corrections used to chase, one measured number per mark, never converging — there are
 * always more families than corrections, and each number is measured against a different
 * reference. Making the box BE the ink removes the per-family variable instead of correcting it.
 *
 * `getBBox()` reports geometry only, so stroke ink spills past it: half the largest computed
 * `stroke-width` among the mark's stroked descendants is added back as padding on all four sides
 * (a stroke straddles its path, so exactly half of it lies outside the geometry box). The
 * width/height presentation attributes the builders mount at their reference size come off, or a
 * host's `width: auto` would resolve to that intrinsic size instead of the fresh viewBox's aspect.
 *
 * Idempotent, and not by a guard: `getBBox` reports the drawn geometry in the mark's own
 * untransformed user space, which a viewBox rewrite does not move — so a second pass measures the
 * same ink and writes the same box. An enhancer that re-runs over already-filled slots needs no
 * bookkeeping here.
 *
 * Three callers, two regimes, and the split is deliberate: this makes the box the ink, and each
 * host decides how that ink meets its line. `hints.js` gives the float's marks `width: auto` at a
 * fixed height, having no reserved box to hold, and `help-enhance.js` sizes its static picture of
 * that same float the same way, so one em number means drawn mark on both; `term-enhance.js`
 * bottom-anchors them inside the reserved CLS box it must keep byte-identical (skat.css
 * § Glossary).
 *
 * Requires an ATTACHED, rendered mark — `getBBox` reads zeros off the DOM — so all three callers
 * run this after their mount lands in the document. jsdom implements no SVG geometry at all, so a
 * suite rendering a mark through any of those paths stubs `getBBox` (`hints.fixtures.js`'s own comment
 * carries the reasoning) rather than this module guarding a capability production never lacks.
 */
export function frameToInk(mark) {
  const ink = mark.getBBox();
  let strokeWidth = 0;
  for (const node of mark.querySelectorAll("*")) {
    const style = getComputedStyle(node);
    if (style.stroke !== "none")
      strokeWidth = Math.max(strokeWidth, parseFloat(style.strokeWidth) || 0);
  }
  const pad = strokeWidth / 2;
  mark.setAttribute(
    "viewBox",
    `${ink.x - pad} ${ink.y - pad} ${ink.width + 2 * pad} ${ink.height + 2 * pad}`,
  );
  mark.removeAttribute("width");
  mark.removeAttribute("height");
}
